import { useEffect, useState } from 'react';
import { MemberProfileData } from '../models/member';
import { MemberDetailProfileGroupDisplay } from './memberDetailProfileGroupDisplay';

type MemberDetailProfileGroupSummaryProps = {
  profileData: MemberProfileData[] | undefined;
  maxItemsShown?: number;
};

export const MemberDetailProfileGroupSummary = (props: MemberDetailProfileGroupSummaryProps) => {
  const { profileData, maxItemsShown = 3 } = props;

  const [groupedProfileData, setGroupedProfileData] = useState({});

  // Group profile data when member.profileData changes
  useEffect(() => {
    if (profileData) {
      // Group profile data by groupName
      const grouped = profileData.reduce((acc, item) => {
        const group = acc[item.groupName] || [];
        group.push(item);
        acc[item.groupName] = group;
        return acc;
      }, {});

      // Custom sorting function, thank you ChatGPT :-D
      // Order strings alphabetically and order numbers descending, like 'A 42', 'A 41', 'B 2', 'B 1'
      const customSort = (a, b) => {
        const extractBaseString = (str) => str.replace(/(\d+)/g, '').trim();
        const extractNumber = (str) => {
          const match = str.match(/(\d+)/);
          return match ? parseInt(match[0], 10) : 0;
        };

        const aBase = extractBaseString(a.itemName);
        const bBase = extractBaseString(b.itemName);

        // Compare the base strings alphabetically
        const baseComparison = aBase.localeCompare(bBase, undefined, {
          numeric: false,
          sensitivity: 'base',
        });

        if (baseComparison !== 0) {
          return baseComparison;
        }

        // If base strings are the same, compare by numbers (desc)
        const aNumber = extractNumber(a.itemName);
        const bNumber = extractNumber(b.itemName);

        return bNumber - aNumber;
      };

      // Sort items within each group
      Object.keys(grouped).forEach((groupName) => {
        grouped[groupName].sort(customSort);
      });

      setGroupedProfileData(grouped);
    }
  }, [profileData]);

  if (profileData === undefined) return null;

  return (
    <>
      {Object.keys(groupedProfileData).map((groupName) => {
        return (
          <MemberDetailProfileGroupDisplay
            label={groupName}
            childProfileItems={groupedProfileData[groupName].sort((a, b) => a.pos - b.pos)}
            key={groupedProfileData[groupName][0].memberProfile_id}
            fullWidth
            maxItemsShown={maxItemsShown || 3}
          />
        );
      })}
    </>
  );
};
